import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'hooks/redux';
import Wallet from 'modules/Wallet';
import { formatCurrency } from 'lib/formatters';
import { promotionCode as promotionCodeValidation } from 'lib/valFuncs';
import { depositAmount as depositAmountValidation } from 'lib/valFunctors';
import { isLoading } from 'lib/redux-utils';
import Api from 'services/Api';
import Auth from 'modules/Auth';
import { paymentMethodProvider, paymentMethodType } from 'lib/constants';
import FundsProtectionCheck from 'components/FundsProtectionCheck';
// THIS NEEDS TO BE MOVED TO ANOTHER LOCATION
import '../Deposit/src/depositCardForm.css';
import TrueLayerForm from './TrueLayerForm';

interface TrueLayerFormAdapterProps {
  promoCode?: string;
  disabled?: boolean;
  isDisabled?: boolean;
  isPromoCodeDisabled: boolean;
  disableDepositButton?: boolean;
  paymentMethodRef?: string;
  isDepositWelcomeOfferVisible: boolean;
  onComplete: (shouldPreventClose: boolean) => void;
}

const TrueLayerFormAdapter = ({
  promoCode,
  paymentMethodRef,
  disabled,
  isDisabled,
  isPromoCodeDisabled,
  disableDepositButton,
  isDepositWelcomeOfferVisible,
  onComplete
}: TrueLayerFormAdapterProps) => {
  const defaultAmount = useAppSelector(
    (state) =>
      Wallet.selectors.getDefaultDepositAmountForPaymentType(
        state,
        paymentMethodType.BANK_ACCOUNT
      ) as number[] | undefined
  );
  const minAmount = useAppSelector(
    (state) =>
      Wallet.selectors.getMinAmountForPaymentType(state, paymentMethodType.BANK_ACCOUNT) as
        | number
        | undefined
  );
  const maxAmount = useAppSelector(
    (state) =>
      Wallet.selectors.getMaxAmountForPaymentType(state, paymentMethodType.BANK_ACCOUNT) as
        | number
        | undefined
  );
  const hasAgreedPaymentTerms = useAppSelector(Wallet.selectors.hasAgreedPaymentTerms);
  const isLoadingData = useAppSelector((state) =>
    isLoading(state, [Wallet.actionTypes.AT.DEPOSIT_TRUE_LAYER._, Auth.AT.FINGERPRINT._])
  );
  const dispatch = useDispatch();
  const [acceptFundsProtection, setAcceptFundsProtection] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fundsProtectionCheck = !hasAgreedPaymentTerms && (
    <FundsProtectionCheck
      onChange={() => setAcceptFundsProtection((s) => !s)}
      checked={acceptFundsProtection}
    />
  );

  const fields = useMemo(
    () => ({
      depositAmount: {
        initial: defaultAmount || 50,
        required: true,
        error: `Minimum amount ${formatCurrency(10)}`,
        onChange: depositAmountValidation(minAmount, maxAmount)
      },
      promotionCode: {
        initial: promoCode || '',
        required: false,
        onChange: promotionCodeValidation,
        error: "This doesn't look like a valid code"
      }
    }),
    [defaultAmount, maxAmount, minAmount, promoCode]
  );

  const onSubmit = useCallback(
    async ({ depositAmount, promotionCode }: { depositAmount: number; promotionCode: string }) => {
      setErrorMessage(null);
      const fingerprintResult = dispatch(
        Auth.actions.getDeviceFingerprint()
      ) as unknown as Promise<{ value: any }>;
      const { value: deviceInfo } = await fingerprintResult;

      try {
        await Api.actions.wallet.depositTrueLayer(null, {
          paymentMethodRef: paymentMethodRef || null,
          provider: paymentMethodProvider.TRUELAYER,
          amount: depositAmount,
          promotionCode: promotionCode,
          deviceInfo,
          agreeDepositTerms: acceptFundsProtection
        })(dispatch, true);
        onComplete(true);
      } catch (e: any) {
        setErrorMessage(e.msg);
      }
    },
    [acceptFundsProtection, dispatch, onComplete, paymentMethodRef]
  );

  return (
    <TrueLayerForm
      fields={fields}
      minAmount={minAmount}
      maxAmount={maxAmount}
      actionRequestErrorMessage={errorMessage}
      loading={isLoadingData}
      disabled={disabled}
      fundsProtectionCheck={fundsProtectionCheck}
      isPromoCodeDisabled={isPromoCodeDisabled}
      isDisabled={isDisabled}
      isDepositWelcomeOfferVisible={isDepositWelcomeOfferVisible}
      disableDepositButton={
        disableDepositButton || !(hasAgreedPaymentTerms || acceptFundsProtection)
      }
      submit={onSubmit}
    />
  );
};

export default memo(TrueLayerFormAdapter);
